import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import { PublicHeader, PublicFooter } from "@/components/PublicHeader";
import { HeroDemo } from "@/components/HeroDemo";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import {
  Globe, Code2, Bot, Smartphone, Server, Sparkles, ArrowRight, Check,
  Zap, ShieldCheck, Gift, Star,
} from "lucide-react";

const FEATURES = [
  { icon: Globe, title: "Websites", desc: "Landing pages, portfolios & full sites with clean, responsive code." },
  { icon: Smartphone, title: "Applications", desc: "Web & mobile apps, dashboards and internal tools — front to back." },
  { icon: Server, title: "Backends & APIs", desc: "Databases, REST APIs and auth wired up in minutes." },
  { icon: Bot, title: "AI agents", desc: "Autonomous agents that use tools to get real work done." },
];

const MODELS = [
  { name: "Gemini 3.1 Pro", provider: "Google", cost: "1 credit / msg", dot: "#4285F4" },
  { name: "GPT-5.4", provider: "OpenAI", cost: "2 credits / msg", dot: "#10A37F" },
  { name: "Claude Sonnet 4.6", provider: "Anthropic", cost: "3 credits / msg", dot: "#D97757" },
];

const STEPS = [
  { n: "01", t: "Sign up free", d: "Create an account and get 50 free credits instantly." },
  { n: "02", t: "Describe your idea", d: "Tell NIGAMAI what to build in plain English." },
  { n: "03", t: "Ship it", d: "Copy the generated code and go live." },
];

const STATS = [
  { v: "50", l: "free credits on signup" },
  { v: "3", l: "frontier AI models" },
  { v: "∞", l: "things you can build" },
  { v: "24/7", l: "always available" },
];

const TESTIMONIALS = [
  { q: "I shipped my startup's landing page in an afternoon. It genuinely feels like having a senior engineer on call.", n: "Aarav M.", r: "Indie Founder" },
  { q: "Switching between Gemini, GPT and Claude in one place is a game changer. The code quality is unreal.", n: "Sofia R.", r: "Full-stack Dev" },
  { q: "From idea to working API before my coffee got cold. This is the future of building software.", n: "Daniel K.", r: "Product Engineer" },
];

const PACK_LABEL = { p200: "Basic", p500: "Pro", p1000: "Power" };

export default function Landing() {
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    api.get("/payments/packs").then((r) => setPacks(r.data)).catch(() => {});
  }, []);

  const preview = packs.filter((p) => ["p200", "p500", "p1000"].includes(p.id));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <PublicHeader />

      {/* ============ HERO ============ */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={ASSETS.heroBg} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-[#0A0A0A]/80 to-[#0A0A0A]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-2 gap-16 items-center">
          <div className="stagger">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-[#141415]/80 text-xs text-zinc-300 mb-7">
              <Gift className="w-3.5 h-3.5 text-[#00FF66]" />
              50 free credits on signup — no card required
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-6xl tracking-tighter leading-[1.02]">
              The AI that <span className="text-[#00FF66] text-glow">codes anything</span> you can imagine.
            </h1>
            <p className="mt-6 text-lg text-zinc-400 max-w-xl leading-relaxed">
              Websites, apps, backends and AI agents — built for you in minutes by Claude, GPT and Gemini.
              One elegant platform. Pro-grade power. Free to start.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="hero-get-started"
                onClick={() => navigate("/login")}
                className="h-12 px-8 bg-[#00FF66] hover:bg-[#00CC52] text-black font-semibold rounded-xl text-base transition-colors group shadow-[0_0_40px_rgba(0,255,102,0.25)]"
              >
                Start building free
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <Button
                data-testid="hero-pricing"
                onClick={() => navigate("/pricing")}
                variant="outline"
                className="h-12 px-8 bg-transparent border-white/15 hover:bg-[#141415] text-white rounded-xl text-base transition-colors"
              >
                View pricing
              </Button>
            </div>
            <div className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-zinc-400">
              <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#00FF66]" /> Instant streaming</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#00FF66]" /> Secure & private</span>
              <span className="flex items-center gap-2"><Star className="w-4 h-4 text-[#00FF66]" /> 3 frontier models</span>
            </div>
          </div>

          <HeroDemo />
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section className="border-y border-white/10 bg-[#0D0D0E]">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <p className="text-center text-xs text-zinc-500 uppercase tracking-[0.2em] mb-6">
            Powered by the world's most advanced models
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {MODELS.map((m) => (
              <div key={m.name} className="flex items-center gap-2.5 px-5 py-2.5 rounded-full border border-white/10 bg-[#141415]">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: m.dot }} />
                <span className="text-sm font-medium text-white">{m.name}</span>
                <span className="text-xs text-zinc-500">· {m.provider}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ STATS ============ */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {STATS.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="text-center p-6 rounded-2xl bg-[#141415] border border-white/10">
                <div className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tighter text-[#00FF66]">{s.v}</div>
                <div className="text-sm text-zinc-400 mt-2">{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ BENTO FEATURES ============ */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-16">
        <Reveal>
          <div className="max-w-2xl">
            <p className="text-[#00FF66] text-sm font-mono mb-3">// what you can build</p>
            <h2 className="font-heading font-bold text-3xl sm:text-5xl tracking-tighter">
              One assistant. Every kind of code.
            </h2>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-12">
          <Reveal className="lg:row-span-2">
            <div className="relative rounded-3xl border border-white/10 overflow-hidden h-full min-h-[320px] group">
              <img src={ASSETS.featureAi} alt="AI brain" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-end p-8">
                <h3 className="font-heading font-bold text-2xl text-white">Frontier intelligence</h3>
                <p className="text-zinc-300 mt-2 max-w-xs leading-relaxed">
                  Reasoning that plans architecture, writes code and fixes its own mistakes.
                </p>
              </div>
            </div>
          </Reveal>

          {FEATURES.map((f, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div
                data-testid={`feature-card-${i}`}
                className="group h-full p-6 rounded-3xl bg-[#141415] border border-white/10 hover:border-[#00FF66]/40 transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-[#00FF66]/10 flex items-center justify-center mb-4 group-hover:bg-[#00FF66]/20 transition-colors">
                  <f.icon className="w-5 h-5 text-[#00FF66]" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white">{f.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ SHOWCASE BAND ============ */}
      <section className="relative border-y border-white/10 overflow-hidden">
        <img src={ASSETS.showcaseBg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-[#0A0A0A]/70" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div>
              <p className="text-[#00FF66] text-sm font-mono mb-3">// no limits</p>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tighter">
                If you can describe it, NIGAMAI can build it.
              </h2>
              <p className="text-zinc-300 mt-4 leading-relaxed max-w-lg">
                Skip the boilerplate and the blank page. Go from a single sentence to shippable code —
                with syntax highlighting, copy-ready blocks and multi-turn context.
              </p>
            </div>
          </Reveal>
          <div className="space-y-3">
            {["Production-ready code, every time", "Upload images & screenshots for context", "Full conversation history, saved securely", "Switch models mid-conversation"].map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-[#141415]/80 border border-white/10">
                  <div className="w-8 h-8 rounded-lg bg-[#00FF66]/10 flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4 text-[#00FF66]" />
                  </div>
                  <span className="text-sm text-zinc-200">{t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ MODELS DETAIL ============ */}
      <section id="models" className="max-w-7xl mx-auto px-6 py-24">
        <Reveal>
          <div className="max-w-2xl">
            <p className="text-[#00FF66] text-sm font-mono mb-3">// choose your brain</p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tighter">
              Switch between the best models, instantly.
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
          {MODELS.map((m, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="relative p-8 rounded-3xl bg-[#141415] border border-white/10 hover:border-[#00FF66]/40 transition-colors">
                <span className="w-3 h-3 rounded-full block mb-5" style={{ background: m.dot, boxShadow: `0 0 20px ${m.dot}` }} />
                <p className="text-xs text-zinc-500 font-mono">{m.provider}</p>
                <h3 className="font-heading font-bold text-2xl text-white mt-1">{m.name}</h3>
                <p className="text-[#00FF66] text-sm font-mono mt-4">{m.cost}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="border-y border-white/10 bg-[#0D0D0E]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <Reveal>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tighter text-center">
              From idea to shipped in 3 steps.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14">
            {STEPS.map((s, i) => (
              <Reveal key={i} delay={i * 0.12}>
                <div>
                  <span className="font-heading font-extrabold text-6xl text-white/5">{s.n}</span>
                  <h3 className="font-heading font-semibold text-xl text-white -mt-6 relative">{s.t}</h3>
                  <p className="text-zinc-400 mt-2 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <Reveal>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tighter text-center">
            Loved by builders everywhere.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="h-full p-7 rounded-3xl bg-[#141415] border border-white/10">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, s) => <Star key={s} className="w-4 h-4 text-[#00FF66] fill-[#00FF66]" />)}
                </div>
                <p className="text-zinc-200 leading-relaxed">"{t.q}"</p>
                <div className="mt-5 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#00FF66] flex items-center justify-center text-black font-semibold text-sm">
                    {t.n.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">{t.n}</p>
                    <p className="text-xs text-zinc-500">{t.r}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ PRICING PREVIEW ============ */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <Reveal>
          <div className="text-center max-w-xl mx-auto">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl tracking-tighter">Simple, fair pricing.</h2>
            <p className="text-zinc-400 mt-3">Start with 50 free credits. Top up anytime — credits never expire.</p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12">
          {preview.map((p, i) => {
            const popular = p.id === "p500";
            return (
              <Reveal key={p.id} delay={i * 0.1}>
                <div
                  className={`relative flex flex-col h-full p-7 rounded-3xl border transition-colors ${
                    popular ? "border-[#00FF66] bg-[#101a12]" : "border-white/10 bg-[#141415]"
                  }`}
                >
                  {popular && (
                    <span className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-[#00FF66] text-black text-xs font-semibold">
                      Most popular
                    </span>
                  )}
                  <p className="text-sm text-zinc-500">{PACK_LABEL[p.id]}</p>
                  <div className="mt-3 font-heading font-extrabold text-4xl tracking-tighter">₹{p.amount}</div>
                  <p className="text-[#00FF66] font-mono text-sm mt-2">{p.credits.toLocaleString()} credits</p>
                  <Button
                    onClick={() => navigate("/login")}
                    className={`mt-6 h-11 rounded-lg font-semibold transition-colors ${
                      popular ? "bg-[#00FF66] hover:bg-[#00CC52] text-black" : "bg-[#1F1F22] hover:bg-[#2a2a2e] text-white"
                    }`}
                  >
                    Get started
                  </Button>
                </div>
              </Reveal>
            );
          })}
        </div>
        <div className="text-center mt-8">
          <button onClick={() => navigate("/pricing")} className="text-sm text-[#00FF66] hover:text-[#00CC52] transition-colors inline-flex items-center gap-1.5">
            See all plans & corporate <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <Reveal>
          <div className="relative rounded-[2rem] border border-[#00FF66]/20 overflow-hidden p-12 sm:p-20 text-center">
            <img src={ASSETS.ctaBg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
            <div className="absolute inset-0 bg-[#0A0A0A]/50" />
            <div className="relative z-10">
              <h2 className="font-heading font-extrabold text-3xl sm:text-6xl tracking-tighter text-glow">
                Build something great today.
              </h2>
              <p className="text-zinc-300 mt-5 max-w-xl mx-auto text-lg">
                Join now, grab your 50 free credits, and ship your first project in the next 60 seconds.
              </p>
              <Button
                data-testid="cta-get-started"
                onClick={() => navigate("/login")}
                className="mt-9 h-12 px-10 bg-[#00FF66] hover:bg-[#00CC52] text-black font-semibold rounded-xl text-base transition-colors shadow-[0_0_50px_rgba(0,255,102,0.35)]"
              >
                Get started free
              </Button>
            </div>
          </div>
        </Reveal>
      </section>

      <PublicFooter />
    </div>
  );
}
